import { useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Flex,
  Text,
} from '@backstage/ui';
import { RiUploadLine } from '@remixicon/react';
import type { TrackImportResult } from '@internal/backstage-plugin-pulse-common';
import { useImportTracks, usePulseChecks } from '../../hooks';
import { isTrackImportBatchError } from '../../hooks/use-import-tracks';
import { trackImportDefinitionSchema } from '../../schemas/tracks';
import { parseYamlDefinitions } from '../yaml-import';

export type TrackImportPreview = {
  id?: string;
  name?: string;
  errors: string[];
  missingCheckIds: string[];
};

type TrackImportDialogProps = {
  onImportStarted: () => void;
  onImported: (result: TrackImportResult) => void;
};

const candidateString = (
  candidate: unknown,
  key: 'id' | 'name',
): string | undefined => {
  if (candidate !== null && typeof candidate === 'object') {
    const candidateRecord = candidate as Record<string, unknown>;
    if (typeof candidateRecord[key] === 'string') {
      return candidateRecord[key];
    }
  }
  return undefined;
};

export const analyzeTrackDefinitions = (
  definitions: unknown[],
  knownCheckIds: ReadonlySet<string>,
): TrackImportPreview[] =>
  definitions.map(candidate => {
    const parsed = trackImportDefinitionSchema.safeParse(candidate);
    if (!parsed.success) {
      return {
        id: candidateString(candidate, 'id'),
        name: candidateString(candidate, 'name'),
        errors: parsed.error.issues.map(issue => {
          const location = issue.path.length ? `${issue.path.join('.')}: ` : '';
          return `${location}${issue.message}`;
        }),
        missingCheckIds: [],
      };
    }

    const missingCheckIds = Array.from(
      new Set(
        parsed.data.levels.flatMap(level =>
          level.checkIds.filter(checkId => !knownCheckIds.has(checkId)),
        ),
      ),
    ).sort((left, right) => left.localeCompare(right));

    return {
      id: parsed.data.id,
      name: parsed.data.name,
      errors: [],
      missingCheckIds,
    };
  });

export const TrackImportDialog = ({
  onImportStarted,
  onImported,
}: TrackImportDialogProps) => {
  const {
    importTracks,
    loading,
    error: importError,
    clearError: clearImportError,
  } = useImportTracks();
  const [isOpen, setIsOpen] = useState(false);
  const [fileName, setFileName] = useState('');
  const [definitions, setDefinitions] = useState<unknown[]>([]);
  const [parseError, setParseError] = useState<string>();
  const {
    value: checks,
    loading: checksLoading,
    error: checksError,
  } = usePulseChecks({ enabled: isOpen });
  const knownCheckIds = useMemo(
    () => new Set((checks ?? []).map(check => check.id)),
    [checks],
  );
  const previews = useMemo(
    () => analyzeTrackDefinitions(definitions, knownCheckIds),
    [definitions, knownCheckIds],
  );

  const reset = (): void => {
    setFileName('');
    setDefinitions([]);
    setParseError(undefined);
    clearImportError();
  };

  const setOpen = (open: boolean): void => {
    setIsOpen(open);
    if (!open) {
      reset();
    }
  };

  const selectFile = async (file: File | undefined): Promise<void> => {
    reset();
    if (!file) {
      return;
    }

    setFileName(file.name);
    try {
      setDefinitions(parseYamlDefinitions(await file.text()));
    } catch (error) {
      setParseError(
        error instanceof Error ? error.message : 'Unable to read YAML file.',
      );
    }
  };

  const submit = async (): Promise<void> => {
    onImportStarted();
    try {
      const result = await importTracks(definitions);
      onImported(result);
      setOpen(false);
    } catch (error) {
      if (isTrackImportBatchError(error) && error.partialResult.total > 0) {
        onImported(error.partialResult);
      }
    }
  };

  return (
    <>
      <Button
        variant="secondary"
        iconStart={<RiUploadLine />}
        onClick={() => setOpen(true)}
      >
        Import tracks
      </Button>
      <Dialog isOpen={isOpen} onOpenChange={setOpen} width={640}>
        <DialogHeader>Import YAML track(s)</DialogHeader>
        <DialogBody>
          <Flex direction="column" gap="4">
            <Text>
              Select one file containing a single or multiple tracks. Existing
              IDs will be skipped.
            </Text>
            <label htmlFor="pulse-track-import-file">
              <Text weight="bold">YAML file</Text>
            </label>
            <input
              id="pulse-track-import-file"
              type="file"
              accept=".yaml,.yml,text/yaml,application/yaml"
              disabled={loading}
              onChange={event => {
                const file = event.currentTarget.files?.[0];
                event.currentTarget.value = '';
                void selectFile(file);
              }}
            />
            {fileName && !parseError ? (
              <Text color="primary">
                {fileName}: {previews.length}{' '}
                {previews.length === 1 ? 'track' : 'tracks'} found
              </Text>
            ) : null}
            {checksLoading ? (
              <Text color="secondary">
                Check references will be verified when checks finish loading.
              </Text>
            ) : null}
            {!checksLoading && checksError ? (
              <Text color="danger" role="alert">
                Check references could not be verified: {checksError.message}
              </Text>
            ) : null}
            {parseError ? (
              <Text color="danger" role="alert">
                {parseError}
              </Text>
            ) : null}
            {importError ? (
              <Text color="danger" role="alert">
                {importError.message}
              </Text>
            ) : null}
            {previews.map((preview, index) => (
              <Flex
                key={`${preview.id ?? 'invalid'}-${index}`}
                direction="column"
                gap="1"
              >
                <Text weight="bold">
                  {preview.name ?? preview.id ?? `Track ${index + 1}`}
                  {preview.id ? ` (${preview.id})` : ''}
                </Text>
                {preview.errors.map(error => (
                  <Text key={error} color="danger">
                    {error}
                  </Text>
                ))}
                {!checksLoading &&
                !checksError &&
                preview.missingCheckIds.length > 0 ? (
                  <Text color="warning">
                    Missing checks: {preview.missingCheckIds.join(', ')}
                  </Text>
                ) : null}
                {!checksLoading &&
                !checksError &&
                preview.errors.length === 0 &&
                preview.missingCheckIds.length === 0 ? (
                  <Text color="success">Definition is ready for import.</Text>
                ) : null}
              </Flex>
            ))}
          </Flex>
        </DialogBody>
        <DialogFooter>
          <Flex justify="end" gap="2">
            <Button
              variant="secondary"
              isDisabled={loading}
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              isPending={loading}
              isDisabled={loading || definitions.length === 0}
              onClick={() => void submit()}
            >
              Import
            </Button>
          </Flex>
        </DialogFooter>
      </Dialog>
    </>
  );
};
